import React from 'react';
import PropTypes from 'prop-types';
import HSL from '@haptik/helpers/build/esm/hslBuilder';
import Button from 'relements/build/esm/ui/button';
import Icon from 'relements/build/esm/ui/icon';
import Table from 'relements/build/esm/ui/table';
import Checkbox from 'relements/build/esm/input/checkbox';
import Text from 'relements/build/esm/input/text';
import Loader from 'relements/build/esm/decorators/loader';

import memoize from 'fast-memoize';

import connection from '@src/connection';
import hashHistory from '@src/History';
import BotStore from '@src/store';

// Local
import {
  updateGeneralResponses,
  sendUpdatedGeneralResponses,
  createAlert,
} from '@src/actions/BotActions';

import * as maps from './maps';
import './GeneralResponses.scss';

const COLUMNS = [
  {
    title: 'Input Responses',
    sortKey: 'inputResponses',
    width: '40%',
  },
  {
    title: 'Output Responses',
    sortKey: 'outputResponses',
    width: '50%',
  },
  {
    title: 'Stop Flag',
    sortKey: 'stopFlag',
    width: '10%',
  },
];

@connection(maps.listenToStores, maps.getStateFromStores)
@Loader({})
export default class GeneralResponses extends React.Component {
  constructor(props) {
    super(props);
    this.update = this.update.bind(this);
    this.generateData = memoize(this.generateData.bind(this));
    this.renderInputResponse = memoize(this.renderInputResponse.bind(this));
    this.renderOutputResponse = memoize(this.renderOutputResponse.bind(this));
    this.renderCheckbox = memoize(this.renderCheckbox.bind(this));
    this.renderHeader = memoize(this.renderHeader.bind(this));
  }

  componentDidMount() {
    this.props.activateLoader();
    maps.getGeneralResponses(
      this.props.match.params.botId,
      this.props.deactivateLoader
    );
  }

  render() {
    return (
      <div className="general-responses-overlay">
        {this.renderHeader(this.props.domain.title)}
        {this.renderTable()}
      </div>
    );
  }

  renderHeader(title) {
    return (
      <div className="general-responses-overlay-header">
        <div className="general-responses-overlay-header-title">
          General Responses for 
          {' '}
          <strong>{title}</strong>
        </div>
        <Button type={Button.TYPES.PRIMARY} onClick={this.update}>
          Save
        </Button>
        <Icon
          className="general-responses-overlay-header-icon"
          src="close"
          onClick={() =>
            hashHistory.push(
              this.props.location.prevLocation
                ? this.props.location.prevLocation
                : `/bot/${this.props.match.params.botId}`
            )}
        />
      </div>
    );
  }

  renderTable() {
    const { generalResponses, additionalRows } = this.props;
    const rowData = this.generateData(generalResponses, additionalRows);
    return (
      <Table
        prefixClassName="general-responses-overlay-table"
        columns={COLUMNS}
        rows={rowData}
      />
    );
  }

  renderInputResponse(id, value) {
    return (
      <Text
        key={`text-${id}`}
        label="User Says"
        value={value}
        onChange={this.handleChange('text', `text-${id}`)}
        className="general-responses-overlay-input"
        placeholder="Enter User Messages"
      />
    );
  }

  renderOutputResponse(id, value) {
    return (
      <HSL
        key={`mult-${id}`}
        value={value}
        onChange={this.handleChange('mult', `mult-${id}`)}
        className="general-responses-overlay-output"
        withVariants
        withReplies
      />
    );
  }

  renderCheckbox(id, value) {
    return (
      <Checkbox.Item
        key={`check-${id}`}
        value={value}
        onChange={this.handleChange('checkbox', `check-${id}`)}
      />
    );
  }

  handleChange = (type, id) => value => {
    updateGeneralResponses({
      type,
      value,
      id,
      edited: true,
      context: this.props.domain.id,
    });
  };

  update() {
    const editedResponses = BotStore.getEditedGeneralResponses();
    for (const key in editedResponses) {
      if (key !== 'domainId') {
        if (editedResponses[key].inputResponse.indexOf('.') != -1) {
          createAlert('Input response cannot contain dots "."');
          return;
        }
      }
    }
    sendUpdatedGeneralResponses(
      BotStore.getGeneralResponses(),
      editedResponses,
      this.props.meta.domain.id
    );
  }

  // generates column data for a table
  generateColumn = content => ({
    content,
  });

  // generates relevant data for the table component
  generateData(generalResponses = [], additionalRows) {
    const additionalResponses = new Array(additionalRows)
      .fill(0)
      .map((_, _index) => ({
        id: generalResponses.length + _index,
        inputResponse: '',
        outputResponse: [],
        stopFlag: false,
      }));

    return generalResponses
      .concat(additionalResponses)
      .map(({ inputResponse, outputResponse, stopFlag, id }, i) => {
        const data = {
          title: inputResponse,
          multipleInput: outputResponse.length > 0 ? outputResponse : [''],
          active: stopFlag,
          id: id || i,
        };
        const row = {
          columns: [],
          disabled: false,
          className: 'general-responses-overlay-table-body-row',
        };
        row.columns.push(
          this.generateColumn(this.renderInputResponse(data.id, data.title))
        );
        row.columns.push(
          this.generateColumn(
            this.renderOutputResponse(data.id, data.multipleInput)
          )
        );
        row.columns.push(
          this.generateColumn(this.renderCheckbox(data.id, data.active))
        );
        return row;
      });
  }
}

GeneralResponses.propTypes = {
  additionalRows: PropTypes.number,
  botId: PropTypes.string,
  domain: PropTypes.object,
  generalResponses: PropTypes.object,
  location: PropTypes.object,
  meta: PropTypes.object,
};

GeneralResponses.defaultProps = {
  additionalRows: 1,
  botId: '',
  domain: {},
  generalResponses: {},
  location: {},
  meta: {},
};
